import { useEffect, useState, useCallback } from "react";
import {
  Plus, LayoutGrid, Table2, Search, TrendingUp, DollarSign,
  Users, Trophy, Calendar, Building2, Loader2, Upload, Download, PhoneCall, ChevronRight, ChevronLeft, AlertTriangle, UserRound,
} from "lucide-react";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import LeadModal from "../components/LeadModal";
import ImportModal from "../components/ImportModal";
import api, { formatError } from "../lib/api";
import { STAGES, STAGE_STYLES, INDUSTRIES, fmtCurrency } from "../lib/constants";

const localTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

// "Today 9:30 PM" / "Tomorrow 8:00 AM" / "Mon Jul 21, 2:00 PM"
const fmtCallbackTime = (iso) => {
  const dt = new Date(iso);
  if (isNaN(dt)) return iso || "";
  const time = dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const dayDiff = Math.round((startOfDay(dt) - startOfDay(new Date())) / 86400000);
  if (dayDiff === 0) return `Today ${time}`;
  if (dayDiff === 1) return `Tomorrow ${time}`;
  if (dayDiff === -1) return `Yesterday ${time}`;
  const dateStr = dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  return `${dateStr}, ${time}`;
};

// Soonest future call-back for a lead; falls back to the latest past one
const nextCallback = (lead) => {
  const cbs = (lead.call_logs || []).map((c) => c.callback_at).filter(Boolean).sort();
  if (!cbs.length) return null;
  const now = Date.now();
  const future = cbs.find((cb) => new Date(cb).getTime() >= now);
  return future || cbs[cbs.length - 1];
};

const isTodayCallback = (cb) => cb && cb.slice(0, 10) === localTodayStr();
const hasTodayCallback = (lead) => (lead.call_logs || []).some((c) => isTodayCallback(c.callback_at));

// Overdue: the lead's next relevant call-back is in the past and it's not closed
const hasOverdueCall = (lead) => {
  if (["Won", "Lost"].includes(lead.status)) return false;
  const cb = nextCallback(lead);
  return !!cb && new Date(cb) < new Date();
};

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-[#131C31] p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${accent}1a`, border: `1px solid ${accent}33` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-slate-400 truncate">{label}</p>
          <p className="font-display font-bold text-lg text-white truncate">{value}</p>
        </div>
      </div>
    </div>
  );
}

function LeadCard({ lead, onClick, onDragStart }) {
  const closed = ["Won", "Lost"].includes(lead.status);
  const cb = nextCallback(lead);
  let scheduleText = null;
  let overdue = false;
  if (cb) {
    overdue = new Date(cb) < new Date() && !closed;
    scheduleText = fmtCallbackTime(cb);
  } else if (lead.next_follow_up_date) {
    overdue = lead.next_follow_up_date < localTodayStr() && !closed;
    scheduleText = lead.next_follow_up_date;
  }
  return (
    <div
      data-testid={`lead-card-${lead.id}`}
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onClick={() => onClick(lead)}
      className="bg-[#0B1120] p-3.5 rounded-lg border border-slate-800 hover:border-amber-500/30 hover:shadow-[0_0_10px_rgba(245,158,11,0.1)] transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-sm text-white leading-tight group-hover:text-amber-400 transition-colors">{lead.company_name}</p>
        {lead.estimated_deal_value > 0 && (
          <span className="text-xs font-mono text-emerald-400 shrink-0">{fmtCurrency(lead.estimated_deal_value)}</span>
        )}
      </div>
      {lead.contact_name && <p className="text-xs text-slate-400 mt-0.5">{lead.contact_name}{lead.title ? ` · ${lead.title}` : ""}</p>}
      {lead.salesperson && (
        <p className="text-[11px] text-amber-400/90 mt-1 inline-flex items-center gap-1"><UserRound className="w-3 h-3" />{lead.salesperson}</p>
      )}
      {lead.industry && <p className="text-[11px] text-slate-500 mt-1.5 inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{lead.industry}</p>}
      {(lead.tags?.length > 0) && (
        <div className="flex flex-wrap gap-1 mt-2">
          {lead.tags.slice(0, 3).map((t) => (
            <span key={t} className="text-[10px] bg-slate-800 text-slate-300 rounded px-1.5 py-0.5">{t}</span>
          ))}
        </div>
      )}
      {scheduleText && (
        <p className={`text-[11px] mt-2 inline-flex items-center gap-1 ${overdue ? "text-red-400" : "text-slate-500"}`}>
          <Calendar className="w-3 h-3" /> {scheduleText}{overdue ? " · overdue" : ""}
        </p>
      )}
    </div>
  );
}

const PAGE_SIZE = 10;

function KanbanColumn({ stage, leads, st, dragOver, resetKey, onDragOver, onDragLeave, onDrop, onCardClick, onDragStart }) {
  const [page, setPage] = useState(0);
  const slug = stage.toLowerCase().replace(/\s+/g, "-");
  const totalPages = Math.max(1, Math.ceil(leads.length / PAGE_SIZE));
  useEffect(() => { setPage(0); }, [resetKey]);
  useEffect(() => { if (page > totalPages - 1) setPage(totalPages - 1); }, [totalPages, page]);
  const start = page * PAGE_SIZE;
  const visible = leads.slice(start, start + PAGE_SIZE);
  return (
    <div
      data-testid={`kanban-column-${slug}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`flex flex-col gap-3 bg-[#131C31] p-3 rounded-xl border transition-colors ${dragOver === stage ? "border-amber-500/60" : "border-slate-800"}`}
    >
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: st.border }} />
          <span className="text-sm font-semibold" style={{ color: st.text }}>{stage}</span>
        </div>
        <span className="text-xs text-slate-500 bg-slate-800 rounded-full px-2 py-0.5">{leads.length}</span>
      </div>
      <div className="flex flex-col gap-2.5 min-h-[60px]">
        {visible.map((lead) => (
          <LeadCard key={lead.id} lead={lead} onClick={onCardClick} onDragStart={onDragStart} />
        ))}
        {leads.length === 0 && <p className="text-xs text-slate-600 text-center py-4">No leads</p>}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-800 pt-2 mt-1">
          <button
            data-testid={`kanban-page-prev-${slug}`}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span data-testid={`kanban-page-indicator-${slug}`} className="text-[11px] text-slate-500">
            Page {page + 1} of {totalPages}
          </span>
          <button
            data-testid={`kanban-page-next-${slug}`}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("kanban");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalNonce, setModalNonce] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [industryFilter, setIndustryFilter] = useState("all");
  const [sort, setSort] = useState("created");
  const [dragOver, setDragOver] = useState(null);
  const [todayOnly, setTodayOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [salespersonFilter, setSalespersonFilter] = useState("all");
  const [salespeople, setSalespeople] = useState([]);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (industryFilter !== "all") params.industry = industryFilter;
      params.sort = sort;
      const [leadsRes, statsRes, upcomingRes] = await Promise.all([
        api.get("/leads", { params }),
        api.get("/leads/stats"),
        api.get("/leads/upcoming-calls"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setUpcoming(upcomingRes.data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [industryFilter, sort]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/salespeople").then(({ data }) => setSalespeople(data)).catch(() => {});
  }, []);

  const filtered = leads.filter((l) => {
    if (salespersonFilter !== "all" && (l.salesperson || "") !== salespersonFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return [l.company_name, l.contact_name, l.email, l.industry].some((v) => (v || "").toLowerCase().includes(q));
  });

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const upcomingCalls = [...upcoming]
    .filter((c) => {
      const dt = new Date(c.callback_at);
      return !isNaN(dt) && dt >= startOfToday;
    })
    .sort((a, b) => new Date(a.callback_at) - new Date(b.callback_at));

  const todaysCallsCount = upcoming.filter((c) => isTodayCallback(c.callback_at)).length;
  const overdueCount = leads.filter(hasOverdueCall).length;
  const industryOptions = Array.from(new Set([...INDUSTRIES, ...leads.map((l) => l.industry).filter(Boolean)])).sort();
  let boardLeads = filtered;
  if (todayOnly) boardLeads = filtered.filter(hasTodayCallback);
  else if (overdueOnly) boardLeads = filtered.filter(hasOverdueCall);

  const openNew = () => { setEditing(null); setModalNonce((n) => n + 1); setModalOpen(true); };
  const openLeadTab = (leadId) => { window.open(`/app/leads/${leadId}`, "_blank", "noopener"); };

  const fmtCallback = (iso) => fmtCallbackTime(iso);

  const exportCsv = async () => {
    try {
      const params = {};
      if (industryFilter !== "all") params.industry = industryFilter;
      const res = await api.get("/leads/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "king-of-it-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Export failed");
    }
  };

  const onSaved = () => { setModalOpen(false); load(); };
  const onDeleted = () => { setModalOpen(false); load(); };

  const onDragStart = (e, lead) => { e.dataTransfer.setData("leadId", lead.id); };
  const onDrop = async (e, status) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("leadId");
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.status === status) return;
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/leads/${id}/status`, { status });
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      load();
    }
  };

  return (
    <AppLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto animate-fade-in">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display font-black text-3xl tracking-tight">Leads Pipeline</h1>
            <p className="text-slate-400 text-sm mt-1">Track prospects from first touch to closed deal.</p>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="export-leads-button" onClick={exportCsv} className="bg-slate-800 text-white font-semibold rounded-lg px-4 py-2.5 inline-flex items-center gap-2 text-sm border border-slate-700 hover:bg-slate-700 transition-colors">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button data-testid="import-leads-button" onClick={() => setImportOpen(true)} className="bg-slate-800 text-white font-semibold rounded-lg px-4 py-2.5 inline-flex items-center gap-2 text-sm border border-slate-700 hover:bg-slate-700 transition-colors">
              <Upload className="w-4 h-4" /> Import CSV
            </button>
            <button data-testid="add-lead-button" onClick={openNew} className="btn-primary rounded-lg px-5 py-2.5 inline-flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Add Lead
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard icon={Users} label="Total Leads" value={stats.total_leads} accent="#60A5FA" />
            <StatCard icon={TrendingUp} label="Open Pipeline" value={fmtCurrency(stats.open_value)} accent="#F59E0B" />
            <StatCard icon={Trophy} label="Won Value" value={fmtCurrency(stats.won_value)} accent="#34D399" />
            <StatCard icon={DollarSign} label="Total Value" value={fmtCurrency(stats.total_value)} accent="#A78BFA" />
          </div>
        )}

        {/* Upcoming Calls */}
        <div data-testid="upcoming-calls-section" className="rounded-xl border border-slate-800 bg-[#131C31] p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                <PhoneCall className="w-4 h-4 text-amber-400" />
              </div>
              <h2 className="font-display font-bold text-lg">Upcoming Calls</h2>
              <span className="text-xs text-slate-500 bg-slate-800 rounded-full px-2 py-0.5">{upcomingCalls.length}</span>
            </div>
          </div>
          {upcomingCalls.length === 0 ? (
            <p className="text-sm text-slate-500 py-3 text-center">No calls scheduled. Add a call-back date from a lead's Call Log.</p>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-1">
              {upcomingCalls.map((c, i) => {
                const overdue = new Date(c.callback_at) < new Date();
                const st = STAGE_STYLES[c.status] || STAGE_STYLES.New;
                return (
                  <button
                    key={`${c.lead_id}-${i}`}
                    data-testid={`upcoming-call-${c.lead_id}`}
                    onClick={() => openLeadTab(c.lead_id)}
                    className="text-left shrink-0 w-64 bg-[#0B1120] border border-slate-800 rounded-lg p-3 hover:border-amber-500/30 transition-colors group"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-xs font-semibold inline-flex items-center gap-1 ${overdue ? "text-red-400" : "text-amber-400"}`}>
                        <Calendar className="w-3.5 h-3.5" /> {fmtCallback(c.callback_at)}
                      </span>
                      {overdue && <span className="text-[10px] uppercase tracking-wider text-red-400 bg-red-500/10 rounded-full px-1.5 py-0.5">Due</span>}
                    </div>
                    <p className="font-semibold text-sm text-white mt-1.5 truncate group-hover:text-amber-400 transition-colors">{c.company_name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {c.contact_name && <span className="text-xs text-slate-400 truncate">{c.contact_name}</span>}
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full border shrink-0" style={{ color: st.text, borderColor: st.border, background: st.bg }}>{c.status}</span>
                    </div>
                    {c.note && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{c.note}</p>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              data-testid="lead-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search company, contact, email…"
              className="w-full bg-[#131C31] border border-slate-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <select data-testid="filter-industry" value={industryFilter} onChange={(e) => setIndustryFilter(e.target.value)} className="bg-[#131C31] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500">
            <option value="all">All Industries</option>
            {industryOptions.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
          <select data-testid="filter-salesperson" value={salespersonFilter} onChange={(e) => setSalespersonFilter(e.target.value)} className="bg-[#131C31] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500">
            <option value="all">All Salespeople</option>
            {salespeople.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
          <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-[#131C31] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500">
            <option value="created">Newest first</option>
            <option value="follow_up">Follow-up date</option>
            <option value="value">Deal value</option>
          </select>
          <button
            data-testid="today-calls-filter"
            onClick={() => { setTodayOnly((v) => !v); setOverdueOnly(false); }}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm border transition-colors ${todayOnly ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-slate-700 bg-[#131C31] text-slate-300 hover:bg-slate-800"}`}
          >
            <PhoneCall className="w-4 h-4" />
            Today's Calls
            <span data-testid="today-calls-count" className={`text-xs rounded-full px-1.5 py-0.5 ${todayOnly ? "bg-amber-500/20 text-amber-300" : "bg-slate-800 text-slate-400"}`}>{todaysCallsCount}</span>
          </button>
          <button
            data-testid="overdue-calls-filter"
            onClick={() => { setOverdueOnly((v) => !v); setTodayOnly(false); }}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm border transition-colors ${overdueOnly ? "border-red-500 bg-red-500/10 text-red-400" : "border-slate-700 bg-[#131C31] text-slate-300 hover:bg-slate-800"} ${overdueCount > 0 && !overdueOnly ? "text-red-400/90" : ""}`}
          >
            <AlertTriangle className="w-4 h-4" />
            Overdue
            <span data-testid="overdue-calls-count" className={`text-xs rounded-full px-1.5 py-0.5 ${overdueOnly ? "bg-red-500/20 text-red-300" : overdueCount > 0 ? "bg-red-500/20 text-red-300" : "bg-slate-800 text-slate-400"}`}>{overdueCount}</span>
          </button>
          <div className="flex rounded-lg border border-slate-700 overflow-hidden">
            <button data-testid="view-kanban" onClick={() => setView("kanban")} className={`px-3 py-2 ${view === "kanban" ? "bg-amber-500/15 text-amber-400" : "bg-[#131C31] text-slate-400"}`}>
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button data-testid="view-table" onClick={() => setView("table")} className={`px-3 py-2 ${view === "table" ? "bg-amber-500/15 text-amber-400" : "bg-[#131C31] text-slate-400"}`}>
              <Table2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading leads…
          </div>
        ) : view === "kanban" ? (
          <div data-testid="kanban-board" className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4 items-start">
            {STAGES.map((stage) => (
              <KanbanColumn
                key={stage}
                stage={stage}
                leads={boardLeads.filter((l) => l.status === stage)}
                st={STAGE_STYLES[stage]}
                dragOver={dragOver}
                resetKey={`${search}|${industryFilter}|${salespersonFilter}|${todayOnly}|${overdueOnly}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(stage); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => onDrop(e, stage)}
                onCardClick={(l) => openLeadTab(l.id)}
                onDragStart={onDragStart}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-slate-800 bg-[#131C31] overflow-x-auto">
            <table data-testid="leads-table" className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Salesperson</th>
                  <th className="px-4 py-3">Industry</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Value</th>
                  <th className="px-4 py-3">Next Call</th>
                </tr>
              </thead>
              <tbody>
                {boardLeads.map((lead) => {
                  const st = STAGE_STYLES[lead.status];
                  const cb = nextCallback(lead);
                  return (
                    <tr key={lead.id} data-testid={`lead-row-${lead.id}`} onClick={() => openLeadTab(lead.id)} className="border-b border-slate-800/60 hover:bg-slate-800/40 cursor-pointer">
                      <td className="px-4 py-3 font-semibold text-white">{lead.company_name}</td>
                      <td className="px-4 py-3 text-slate-300">{lead.contact_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-400">{lead.salesperson || "—"}</td>
                      <td className="px-4 py-3 text-slate-400">{lead.industry || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2.5 py-0.5 rounded-full border" style={{ color: st.text, borderColor: st.border, background: st.bg }}>{lead.status}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-400">{lead.estimated_deal_value > 0 ? fmtCurrency(lead.estimated_deal_value) : "—"}</td>
                      <td className="px-4 py-3 text-slate-400">{cb ? fmtCallbackTime(cb) : (lead.next_follow_up_date || "—")}</td>
                    </tr>
                  );
                })}
                {boardLeads.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No leads found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <LeadModal key={modalNonce} lead={editing} onClose={() => setModalOpen(false)} onSaved={onSaved} onDeleted={onDeleted} onRefresh={load} />
      )}
      {importOpen && (
        <ImportModal onClose={() => setImportOpen(false)} onImported={() => { setImportOpen(false); load(); }} />
      )}
    </AppLayout>
  );
}
